import { type Group, type Object3D } from 'three'
import BaseFileLoader from './base-file-loader'
import { ThreeItemKeys, ThreeOriginalFileTypes } from '../../../constants/three'

/**
 * Abstract base class for mesh-based file loaders (STL, OBJ, etc.)
 * that follow a common loading pattern with worker support.
 */
abstract class MeshFileLoader extends BaseFileLoader {
  // STL/OBJ meshes were flat-shaded before their geometry was indexed: a non-indexed triangle
  // soup gives `computeVertexNormals` one normal per face. Indexing shares vertices, so the
  // recomputed normals get averaged (smooth) — which domes the flat caps of shapes like
  // cylinders and reads as a "textured" surface. Keep the faceted look with flat shading; this
  // is display-only, so the geometry stays indexed (lighter to transfer). STEP/3MF keep smooth
  // shading (they extend BaseFileLoader directly and are high-resolution CAD surfaces).
  protected override getStandardMaterial() {
    const material = super.getStandardMaterial()
    material.flatShading = true
    material.needsUpdate = true
    return material
  }

  async load(): Promise<Group> {
    return this.loadWithProgress(async () => {
      if (!this.arrayBuffer) {
        await this.loadArrayBuffer()
      }

      let meshes: Object3D[]

      if (this.shouldUseWorker()) {
        const parsedObjects = await this.loadWithWorker()
        meshes = this.processParsedObjects(parsedObjects)
      }
      else {
        meshes = this.processOnMainThread()
      }

      return this.addMeshesToScene(meshes, this.file, () => ({
        [ThreeItemKeys.ORIGINAL_FILE_TYPE]: this.getOriginalFileType(),
      }))
    })
  }

  /**
   * Get the original file type constant
   * Must be implemented by subclasses
   */
  protected abstract getOriginalFileType(): typeof ThreeOriginalFileTypes[keyof typeof ThreeOriginalFileTypes]

  /**
   * Process parsed objects from worker
   * Must be implemented by subclasses
   */
  protected abstract processParsedObjects(parsedObjects: Object3D[]): Object3D[]

  /**
   * Process file on main thread
   * Must be implemented by subclasses
   */
  protected abstract processOnMainThread(): Object3D[]
}

export default MeshFileLoader
